import { View } from "react-native";
import { memo, useEffect, useMemo, useCallback } from "react";
import AudioToggleButton from "../../../common/audio-toggle-button";
import {
  Select,
  SelectItem,
  SelectGroup,
  SelectContent,
  SelectTrigger,
} from "../../../common/shadcn/select";
import VoiceSelectItem from "./voice-select-item";
import { IVoice } from "../../../../types/voice";
import { useVoiceAudioPlayer } from "../../../../hooks/useVoiceAudioPlayer";
import { useFocusEffect } from "expo-router";
import { useActiveAgent } from "../../../../contexts/ActiveAgentContext";
import { usePostHog } from "posthog-js/react";
import SettingsItem from "../common/settings-item";
import { useSettingsData } from "../../../../contexts/SettingsDataContext";
import { transformVoiceSpeedToBackend } from "../../../../utils/setting-helpers";
import { Mic } from "lucide-react-native";

const voices: IVoice[] = require("../../../../assets/voices.json");

const VoiceList = memo(
  ({
    voices,
    selectedVoiceId,
  }: {
    voices: IVoice[];
    selectedVoiceId: string;
  }) => {
    return (
      <View>
        {voices.map((voice) => (
          <SelectItem
            key={voice.voice_id}
            value={voice.voice_id}
            className={`p-2 hover:bg-gray-50 focus:bg-gray-50 rounded-md border cursor-pointer ${
              voice.voice_id === selectedVoiceId
                ? "border-gray-950"
                : "border-gray-300"
            } `}
          >
            <VoiceSelectItem voice={voice} />
          </SelectItem>
        ))}
      </View>
    );
  }
);

const normalizeVoiceSpeed = (voiceSpeed: number) => {
  return transformVoiceSpeedToBackend(voiceSpeed);
};

const VoiceSetting = () => {
  const { isTestCallActive, settings, handleVoice } = useSettingsData();
  const { activeAgent } = useActiveAgent();
  const {
    isPlaying,
    isLoading,
    currentVoiceId,
    playAudio,
    stopAudio,
    updatePlaybackRate,
  } = useVoiceAudioPlayer();
  const posthog = usePostHog();

  useEffect(() => {
    if (isTestCallActive) {
      stopAudio();
    }
  }, [isTestCallActive, stopAudio]);

  const selectedVoice = useMemo(
    () => voices.find((v) => v.voice_id === settings.voiceId),
    [voices, settings.voiceId]
  );

  const handleAudioPress = useCallback(() => {
    posthog.capture("audio_pressed", {
      status: isPlaying ? "paused" : "played",
      voice_id: settings.voiceId,
    });
    if (selectedVoice?.preview_audio_url) {
      playAudio(
        selectedVoice.preview_audio_url,
        settings.voiceId,
        normalizeVoiceSpeed(settings.voiceSpeed)
      );
    }
  }, [selectedVoice, settings.voiceSpeed, playAudio]);

  // Update playback rate if it changes
  useEffect(() => {
    if (isPlaying) {
      const timeoutId = setTimeout(() => {
        updatePlaybackRate(normalizeVoiceSpeed(settings.voiceSpeed));
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [settings.voiceSpeed, isPlaying, updatePlaybackRate]);

  // Stop audio if the voiceId changes
  useEffect(() => {
    if (currentVoiceId && currentVoiceId !== settings.voiceId && isPlaying) {
      stopAudio();
    }
  }, [settings.voiceId, currentVoiceId, isPlaying, stopAudio]);

  // stop audio if we leave the page or change the agent
  useFocusEffect(
    useCallback(() => {
      return async () => {
        stopAudio();
      };
    }, [activeAgent])
  );

  return (
    <View>
      <SettingsItem
        title="Select Voice"
        description="Choose the voice that will represent your restaurant."
        icon={Mic}
      >
        <View className="flex-row justify-between items-center gap-1">
          <View className="flex-1">
            <Select
              value={settings.voiceId}
              onValueChange={handleVoice}
              disabled={isTestCallActive}
            >
              <SelectTrigger>
                <VoiceSelectItem voice={selectedVoice || null} />
              </SelectTrigger>
              <SelectContent className="z-50 bg-white cursor-pointer">
                <SelectGroup>
                  <VoiceList
                    voices={voices}
                    selectedVoiceId={settings.voiceId}
                  />
                </SelectGroup>
              </SelectContent>
            </Select>
          </View>
          <View className="p-4">
            <AudioToggleButton
              size={28}
              isPlaying={isPlaying && currentVoiceId === settings.voiceId}
              isLoading={isLoading && currentVoiceId === settings.voiceId}
              onPress={handleAudioPress}
              disabled={!selectedVoice?.preview_audio_url || isTestCallActive}
            />
          </View>
        </View>
      </SettingsItem>
    </View>
  );
};

export default memo(VoiceSetting);
